import { useEffect, useState } from "react";
import { FiMapPin } from "react-icons/fi";

const LOCATION_CACHE_KEY = "nabhiverse_current_location_label";
const LOCATION_CACHE_TTL = 10 * 60 * 1000;

const getCachedLocation = () => {
    try {
        const cached = JSON.parse(sessionStorage.getItem(LOCATION_CACHE_KEY) || "null");

        if (cached?.label && Date.now() - cached.timestamp < LOCATION_CACHE_TTL) {
            return cached.label;
        }
    } catch (error) {
        sessionStorage.removeItem(LOCATION_CACHE_KEY);
    }

    return "";
};

const setCachedLocation = (label) => {
    try {
        sessionStorage.setItem(
            LOCATION_CACHE_KEY,
            JSON.stringify({ label, timestamp: Date.now() })
        );
    } catch (error) {
        // Ignore storage errors; the chip can still show the current result.
    }
};

const getLocationName = (results = []) => {
    const components = results[0]?.address_components || [];
    const findPart = (type) =>
        components.find((item) => item.types?.includes(type))?.long_name;

    return (
        findPart("locality") ||
        findPart("administrative_area_level_2") ||
        findPart("administrative_area_level_1") ||
        results[0]?.formatted_address?.split(",")?.[0] ||
        ""
    );
};

const CurrentLocationChip = () => {
    const [label, setLabel] = useState(() => getCachedLocation());

    useEffect(() => {
        let isMounted = true;
        const cachedLabel = getCachedLocation();

        if (cachedLabel) {
            setLabel(cachedLabel);
            return undefined;
        }

        if (!navigator.geolocation) {
            setLabel("");
            return undefined;
        }

        navigator.geolocation.getCurrentPosition(
            async ({ coords }) => {
                const fallbackLabel = `${coords.latitude.toFixed(3)}, ${coords.longitude.toFixed(3)}`;
                const mapKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
                let nextLabel = fallbackLabel;

                if (mapKey) {
                    try {
                        const response = await fetch(
                            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${coords.latitude},${coords.longitude}&key=${mapKey}`
                        );
                        const data = await response.json();
                        nextLabel = getLocationName(data?.results) || fallbackLabel;
                    } catch (error) {
                        nextLabel = fallbackLabel;
                    }
                }

                if (isMounted) {
                    setLabel(nextLabel);
                    setCachedLocation(nextLabel);
                }
            },
            () => {
                if (!isMounted) return;
                setLabel("");
            },
            {
                enableHighAccuracy: false,
                maximumAge: 5 * 60 * 1000,
                timeout: 10000,
            }
        );

        return () => {
            isMounted = false;
        };
    }, []);

    if (!label) {
        return null;
    }

    return (
        <div className="desktop_location" title={label}>
            <FiMapPin />
            <span>{label}</span>
        </div>
    );
};

export default CurrentLocationChip;
